/**
 * API Client for Instagram Feed
 * Handles communication with backend API endpoints
 */

/**
 * API Client class
 */
export class APIClient {
  /**
   * Creates an API client instance
   * @param {string} baseUrl - Base URL for API endpoints
   */
  constructor(baseUrl = '') {
    this.baseUrl = baseUrl;
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Delays execution for specified milliseconds
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Fetches Instagram feed data
   * @param {number} limit - Number of posts to fetch
   * @param {number} attempt - Current retry attempt
   * @returns {Promise<Object>} Feed data
   */
  async fetchFeed(limit = 12, attempt = 1) {
    try {
      const url = `${this.baseUrl}/api/instagram-feed?limit=${limit}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Try to parse error response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // Use default error message if JSON parsing fails
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error(`API request failed (attempt ${attempt}/${this.retryAttempts}):`, error);

      // Retry logic
      if (attempt < this.retryAttempts) {
        console.log(`Retrying in ${this.retryDelay}ms...`);
        await this.delay(this.retryDelay * attempt); // Exponential backoff
        return this.fetchFeed(limit, attempt + 1);
      }

      // Max retries reached, throw error
      throw new Error(`Failed to fetch Instagram feed after ${this.retryAttempts} attempts: ${error.message}`);
    }
  }

  /**
   * Checks health of API endpoint
   * @returns {Promise<boolean>} True if API is healthy
   */
  async checkHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/api/instagram-feed?limit=1`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

export default APIClient;
